import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './DataTable.module.css';

export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  /** 'num' right-aligns with tabular figures. */
  align?: 'num';
  render: (row: T) => ReactNode;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Makes rows focusable; Enter opens too. Clicks on links and buttons inside a row are left alone. */
  onRowClick?: (row: T) => void;
  /** Shown in a single row when `rows` is empty. Defaults to "None." */
  empty?: ReactNode;
  /** Accessible name of the table. */
  caption?: string;
}

const INTERACTIVE = 'a, button, input, select, textarea, label, [role="button"], [role="switch"]';

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  empty,
  caption,
}: DataTableProps<T>) {
  const { t } = useTranslation();

  const onClick = (row: T) => (e: MouseEvent<HTMLTableRowElement>) => {
    if ((e.target as HTMLElement).closest(INTERACTIVE)) return;
    onRowClick?.(row);
  };

  const onKeyDown = (row: T) => (e: KeyboardEvent<HTMLTableRowElement>) => {
    if (e.key === 'Enter' && e.target === e.currentTarget) {
      e.preventDefault();
      onRowClick?.(row);
    }
  };

  return (
    <div className={classes.scroll}>
      <table className={classes.table} aria-label={caption}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={col.align === 'num' ? classes.num : undefined}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className={classes.empty}>
                {empty ?? t('ui.none')}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr
                key={rowKey(row)}
                className={onRowClick ? classes.clickable : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onClick={onRowClick ? onClick(row) : undefined}
                onKeyDown={onRowClick ? onKeyDown(row) : undefined}
              >
                {columns.map((col) => (
                  <td key={col.key} className={col.align === 'num' ? classes.num : undefined}>
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
